import * as Sentry from "@sentry/react"
import type { ErrorEvent, StackFrame } from "@sentry/react"

import { GLITCHTIP_DSN, GLITCHTIP_TRACES_SAMPLE_RATE } from "./config"

const EXTENSION_SCHEME = /^(chrome|moz|safari-web|safari|ms-browser)-extension:\/\//

/**
 * A frame that our own bundle cannot possibly have produced.
 *
 * Two shapes, both attributable to a browser extension's content script:
 *
 * 1. A filename with an extension scheme. Only some browsers expose it.
 * 2. `<anonymous>` **with** a line and column — code belonging to no file, i.e.
 *    injected or `eval`'d. `docker/lessboard/secure_headers.conf.template` serves
 *    `script-src 'self'` with no `unsafe-eval`, so neither is reachable from the
 *    page; extension content scripts are exempt from the page CSP, which is
 *    exactly why they can do it.
 *
 * The line/column check is the load-bearing part. A bare `<anonymous>` with no
 * position is an ordinary native frame (`JSON.stringify`, `Array.forEach`, …) and
 * must not count, or every error that unwinds through a builtin would be dropped.
 */
function isInjectedFrame({ filename, lineno }: StackFrame): boolean {
  if (!filename) return false
  if (EXTENSION_SCHEME.test(filename)) return true
  return filename === "<anonymous>" && lineno !== undefined
}

/**
 * Whether the event was caused by code that is not ours, so it can be dropped
 * before it reaches GlitchTip.
 *
 * Two independent signals, because they catch different shapes:
 *
 * - **An injected frame anywhere in the stack.** An extension that patches a DOM
 *   method throws from inside our call stack, so the event still carries plenty of
 *   own-origin frames — an "is any frame ours?" test does not see it. This is the
 *   signal that catches the `Converting circular structure to JSON` reports, where
 *   a patched `HTMLDivElement.appendChild` ran `JSON.stringify` on the node React
 *   was mounting and choked on its `__reactFiber$…` → `stateNode` cycle.
 * - **No own-origin frame at all.** Catches errors thrown entirely inside
 *   third-party code, which never touch our stack.
 *
 * An event with no frames at all is kept: errors without a stack, non-`Error`
 * throws and `captureMessage` calls must still get through.
 */
export function isThirdPartyEvent(event: ErrorEvent): boolean {
  const frames =
    event.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? []) ?? []
  if (frames.length === 0) return false
  if (frames.some(isInjectedFrame)) return true
  return !frames.some((frame) => frame.filename?.startsWith(window.location.origin))
}

export function initMonitoring() {
  // Only report from real (built) deployments, not local dev.
  if (!GLITCHTIP_DSN || import.meta.env.DEV) return

  Sentry.init({
    dsn: GLITCHTIP_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: GLITCHTIP_TRACES_SAMPLE_RATE,
    // GlitchTip does not support sessions. In SDK v10 the deprecated
    // `autoSessionTracking` flag is gone, so we drop the integration that
    // emits session envelopes instead.
    integrations: (defaults) =>
      defaults.filter((integration) => integration.name !== "BrowserSession"),
    // Browser noise that is never actionable. The circular-structure pattern is
    // deliberately narrow: it only matches when the cycle runs through a React
    // fiber, which nothing in this app serializes — the only `JSON.stringify`
    // calls we make take plain Redux slices and `fp.pick`ed objects. A genuinely
    // circular payload in one of those would still be reported.
    ignoreErrors: [
      /Converting circular structure to JSON[\s\S]*__reactFiber/,
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      /^Script error\.?$/,
    ],
    // Only matches when the extension exposes its URL, and only against the
    // innermost frame that has one, so it does not cover the case above.
    denyUrls: [EXTENSION_SCHEME],
    beforeSend: (event) => (isThirdPartyEvent(event) ? null : event),
  })
}
